import { JobTitle } from "domain/entities/job-title";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { translateText } from "infra/translation/translate";
import JobTitleForm from "presentation/components/JobTitle/JobTitleForm";
import Spinner from "presentation/components/Shared/Spinner";
import {
  useDeleteJobTitleMutation,
  useJobTitleByFilter,
} from "presentation/hooks/use-query-job-title";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";

const JobTitleGrid: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [jobTitle, setJobTitle] = useState<JobTitle>();
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [isFormVisible, setIsFormVisible] = useState(false);

  const {
    data: jobTitlesData,
    isLoading,
    isError,
    error,
  } = useJobTitleByFilter({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
  } as BaseInputFilter);

  const { mutate: deleteJobTitle } = useDeleteJobTitleMutation();

  const handleDelete = (jobTitleId: string) => {
    deleteJobTitle(jobTitleId, {
      onSuccess: () => {
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Cargo eliminado com sucesso.",
          life: 3000,
        });
      },
      onError: (error: any) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail:
            error.response?.data?.message ||
            "Ocorreu um erro ao eliminar o cargo.",
          life: 3000,
        });
      },
    });
  };

  const confirmDelete = (jobTitleId: string) => {
    confirmDialog({
      message: "Tem a certeza que deseja eliminar este cargo?",
      header: "Confirmação de Eliminação",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Sim, eliminar",
      rejectLabel: "Não",
      accept: () => handleDelete(jobTitleId),
    });
  };

  const isPeopleManagerBodyTemplate = (rowData: JobTitle) => {
    return <InputSwitch checked={rowData.isPeopleManager || false} disabled />;
  };

  const actionBodyTemplate = (rowData: JobTitle) => {
    return (
      <div className="spacing-icons">
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => {
            setJobTitle(rowData);
            setIsFormVisible(true);
          }}
        />
        <i
          className="pi pi-trash cursor-pointer"
          onClick={() => confirmDelete(rowData.id!)}
        />
      </div>
    );
  };

  const handleHideForm = () => {
    setIsFormVisible(false);
    setJobTitle(undefined);
  };

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Pesquisar por nome"
        />
      </IconField>

      <Button
        icon="pi pi-plus"
        label="Novo Cargo"
        onClick={() => {
          setJobTitle({ isPeopleManager: false } as JobTitle);
          setIsFormVisible(true);
        }}
      />
    </div>
  );

  if (isLoading) return <Spinner />;
  if (isError) return <p>Ocorreu um erro: {error.message}</p>;

  return (
    <div className="card">
      <DataTable
        value={jobTitlesData?.data || []}
        paginator
        rows={10}
        size="small"
        emptyMessage="Nenhum cargo encontrado."
        globalFilter={globalFilter}
        globalFilterFields={["name"]}
        header={header}
      >
        <Column
          field="name"
          header={translateText("Name", "pt")}
          sortable
          style={{ width: "45%" }}
        />
        <Column
          field="isPeopleManager"
          header="People Manager"
          body={isPeopleManagerBodyTemplate}
          sortable
          style={{ width: "45%" }}
        />
        <Column body={actionBodyTemplate} style={{ width: "8rem" }} />
      </DataTable>
      <Toast ref={toast} />
      <ConfirmDialog />
      <JobTitleForm
        visible={isFormVisible}
        onHide={handleHideForm}
        jobTitle={jobTitle}
      />
    </div>
  );
};
export default JobTitleGrid;
